/**
 * THE THREE VIEWS of Work (simplification, 2026-09-24): one row grammar, read
 * three ways.
 *
 *   stage    the map: sections in pipeline order, what waits on the reader
 *            first inside each, then the most recent activity. Done shows this week
 *            and folds the rest.
 *   waiting  the queue: every open ask addressed to the reader, grouped by
 *            what it asks (approve a plan, answer a stopped run, accept a
 *            proposal, merge), oldest first; asks addressed to somebody else,
 *            Working and Done fold to one line each.
 *   latest   the log: newest event first, with the events since the reader
 *            last looked banded at the top, then Earlier today, then one fold
 *            per older day.
 *
 * Everything here is pure: the list renders these groups and the case file's
 * pager walks the same order, so reading down the page and stepping through
 * the pager can never disagree about what comes next.
 *
 * ONE ROW PER TICKET. A review case (ADR-0032) that names its build item folds
 * into that item as its Review facet; a Fletcher request that names its build
 * item folds in as the QA facet. The fold keys ONLY on an explicit link: a
 * review case watching a human-authored pull request has no build item to fold
 * into and keeps its own row, which is exactly what Proctor is doing.
 */
import type { CodingEvent, WorkItem } from "@yagni/shared";
import { runLogLines } from "./RunSurface.js";
import {
  gateJobLineOf,
  isEnvironmentSetupItem,
  itemWaitingPhrase,
  isFletcherRequestItem,
  isNeedsYou,
  isWaitingOnViewer,
  PIPELINE_STAGE_LABEL,
  PIPELINE_STAGES,
  pipelineStageOf,
  rowAgeOf,
  rowClauseOf,
  rowStateLine,
  reviewWaitFactsOf,
  reviewWaitOf,
  rowStateOf,
  stageOutcomeOf,
  waitingOnWhom,
  WORKING_CLAUSE,
  type PipelineStage,
} from "./workStage.js";

export const WORK_VIEWS = ["stage", "waiting", "latest"] as const;
export type WorkView = (typeof WORK_VIEWS)[number];

/** `?view=`: an unknown or absent value reads as the default, By stage. */
export function parseWorkView(raw: string | null): WorkView {
  return WORK_VIEWS.includes(raw as WorkView) ? (raw as WorkView) : "stage";
}

/* ── the fold: one row per ticket ──────────────────────────────────────── */

/**
 * One list row: a ticket, plus the facets that are the SAME ticket seen from a
 * later stage. The build item is always the anchor: it is the thing that was
 * commissioned, it owns the case file, and it is what a reader means when they
 * say "that ticket".
 */
export interface PipelineRow {
  anchor: WorkItem;
  /** Proctor's case on this build item's pull request, when one is listed. */
  review: WorkItem | null;
  /** Fletcher's verification request against this build item, when listed. */
  qa: WorkItem | null;
}

function isTerminal(item: WorkItem): boolean {
  return item.phase === "done" || item.phase === "dismissed";
}

/**
 * The work item a facet explicitly names. `buildWorkItemId` is ADR-0032's
 * linkage; `workItemId` is accepted because a payload that names its subject
 * that way means the same thing. A name that is not in this list resolves to
 * nothing: an unlisted anchor cannot be folded into.
 */
const ANCHOR_LINK_KEYS = ["buildWorkItemId", "workItemId"] as const;

function linkedAnchorId(item: WorkItem, byId: Map<string, WorkItem>): string | null {
  const ref = item.sourceRef ?? {};
  for (const key of ANCHOR_LINK_KEYS) {
    const raw = ref[key];
    if (typeof raw === "string" && raw && raw !== item.id && byId.has(raw)) return raw;
  }
  return null;
}

/** Whether a facet names this anchor, by the same keys the fold reads. */
export function namesAnchor(facet: WorkItem, anchorId: string): boolean {
  return facet.id !== anchorId && linkedAnchorId(facet, new Map([[anchorId, facet]])) === anchorId;
}

/**
 * Fletcher's request items carry only their own `workerRequestId`, so the
 * pull request is the only thing they and a build item provably share. Matched
 * on the number or the URL, never on the title.
 */
function prMatchedAnchorId(item: WorkItem, candidates: WorkItem[]): string | null {
  if (item.prUrl == null && item.prNumber == null) return null;
  const match = candidates.find(
    (other) =>
      other.id !== item.id &&
      other.kind !== "review" &&
      ((item.prUrl != null && other.prUrl === item.prUrl) ||
        (item.prNumber != null && other.prNumber === item.prNumber)),
  );
  return match?.id ?? null;
}

/** Whether a facet names a ticket at all, listed here or not. */
function namesSomeAnchor(item: WorkItem): boolean {
  const ref = item.sourceRef ?? {};
  return ANCHOR_LINK_KEYS.some((key) => {
    const raw = ref[key];
    return typeof raw === "string" && raw !== "" && raw !== item.id;
  });
}

/**
 * Fold the feed into one row per ticket. Environment setup never lists.
 *
 * Every Fletcher check of a ticket's pull request belongs to the ticket
 * (YAG-1077): the newest is its QA facet, the older ones fold away with it,
 * and a check whose ticket this list does not carry (archived, on another
 * Team) prints no row of its own either; the ticket's Fletcher card shows it.
 * A check of a pull request no ticket built keeps its own row.
 */
export function foldPipelineRows(items: WorkItem[]): PipelineRow[] {
  const visible = items.filter((item) => !isEnvironmentSetupItem(item));
  const byId = new Map(visible.map((item) => [item.id, item]));
  const reviewFacets = new Map<string, WorkItem>();
  const qaFacets = new Map<string, WorkItem>();
  const folded = new Set<string>();

  for (const item of visible) {
    if (item.kind !== "review") continue;
    if (isFletcherRequestItem(item)) {
      const anchorId = linkedAnchorId(item, byId) ?? prMatchedAnchorId(item, visible);
      if (anchorId) {
        const held = qaFacets.get(anchorId);
        if (!held || timeOf(activityAtOf(item)) > timeOf(activityAtOf(held)))
          qaFacets.set(anchorId, item);
        folded.add(item.id);
      } else if (namesSomeAnchor(item)) {
        folded.add(item.id);
      }
      continue;
    }
    const anchorId = linkedAnchorId(item, byId);
    if (anchorId && !reviewFacets.has(anchorId)) {
      reviewFacets.set(anchorId, item);
      folded.add(item.id);
    }
  }

  return visible
    .filter((item) => !folded.has(item.id))
    .map((anchor) => ({
      anchor,
      review: reviewFacets.get(anchor.id) ?? null,
      qa: qaFacets.get(anchor.id) ?? null,
    }));
}

/**
 * The one row an anchor folds into, from the same list the board folds: the
 * ticket sheet reads this so the drawer and the row speak for the same item.
 * An anchor the list does not carry (a deep link read on its own) is a row of
 * its own until the list arrives; an item that is itself a facet stands alone.
 */
export function pipelineRowFor(anchor: WorkItem, items: readonly WorkItem[]): PipelineRow {
  const listed = items.some((item) => item.id === anchor.id)
    ? items.map((item) => (item.id === anchor.id ? anchor : item))
    : [anchor, ...items];
  const row = foldPipelineRows(listed).find((entry) => entry.anchor.id === anchor.id);
  return row ?? { anchor, review: null, qa: null };
}

/**
 * Which of a row's items is speaking right now: the latest facet that has not
 * settled, else the anchor. A build whose pull request is under review reads as
 * Review with the reviewer's own gate and run; once the case closes the row
 * falls back to the build item, which is where the work actually is.
 *
 * An idle facet does not speak over a build item that has news. A review case
 * stays open until its pull request closes (ADR-0032), so once Proctor's run
 * finishes the case reads "queued" while the build item is running Wright's fix
 * round or holding Proctor's decision. The facet keeps the row only while it is
 * live, waiting or stopped itself, or while the build item is idle too.
 *
 * Both idle, an idle review case still yields to a build item that knows more
 * about the pull request. The pull request's facts (draft, Proctor's state,
 * GitHub's approval and mergeability) ride the build item, and a review case
 * often carries none: a case with no pull request facts, over a build item
 * whose pull request GitHub approved and merges cleanly, read "Review ·
 * reviewed" under Working instead of Ready to merge (staging, 2026-10-22,
 * NMT-99). A case whose own verdict failed keeps the row: that is news the
 * build item does not carry.
 *
 * THE TICKET DRIVES (YAG-1079, ENT-1100 on prod, 2026-10-28). A ticket that
 * is working or waiting on a person speaks for itself: Proctor and Fletcher
 * are activity inside it (`workingItemsOf`), never its header. And a check
 * that finished never takes over: a settled Fletcher case put a ticket Wright
 * was fixing under Done as "QA · check complete", with Fletcher on its chip.
 */
export function driverOf(row: PipelineRow): WorkItem {
  const anchorState = rowStateOf(row.anchor);
  if (anchorState === "working" || anchorState === "waiting") return row.anchor;
  const anchorIdle = anchorState === "queued";
  const speaks = (facet: WorkItem | null): facet is WorkItem => {
    if (facet == null || isTerminal(facet)) return false;
    const state = rowStateOf(facet);
    return state !== "settled" && (anchorIdle || state !== "queued");
  };
  if (speaks(row.qa)) return row.qa;
  if (speaks(row.review) && !anchorKnowsMoreThan(row.anchor, row.review)) return row.review;
  return row.anchor;
}

/**
 * Everyone working on a row's ticket right now, in the order the row names
 * them: the ticket's own run, then Proctor's review, then Fletcher's check.
 * Two can work at once (Proctor reviewing the head Fletcher is testing), and
 * the row and the sheet say both.
 */
export function workingItemsOf(row: PipelineRow): WorkItem[] {
  const working = [row.anchor, row.review, row.qa].filter(
    (item): item is WorkItem => item != null && !isTerminal(item) && rowStateOf(item) === "working",
  );
  return working.filter((item, index) => working.indexOf(item) === index);
}

/**
 * The item whose stage a row's tag and the sheet's stage button name: the
 * ticket's own while Fletcher tests its pull request, since a check is
 * activity inside the ticket's stage, not a stage of its own.
 */
export function rowStageItemOf(row: PipelineRow): WorkItem {
  const driver = driverOf(row);
  return driver === row.qa ? row.anchor : driver;
}

/**
 * An idle review case with no pull request facts, folded on a build item whose
 * facts say what the pull request waits on.
 */
function anchorKnowsMoreThan(anchor: WorkItem, review: WorkItem): boolean {
  return (
    rowStateOf(review) === "queued" &&
    review.pullRequest == null &&
    stageOutcomeOf(review) !== "failed" &&
    reviewWaitOf(reviewWaitFactsOf(anchor)) !== null
  );
}

/**
 * Whether a row is work still owed, for the Team chip counts. A settled row is
 * not, and neither is a pull request review standing on its own: Proctor opens
 * one per pull request, and they would bury the Team's real tickets. A review
 * folded under its build item counts through that item.
 */
export function isOpenRow(row: PipelineRow): boolean {
  if (row.anchor.kind === "review" && !isFletcherRequestItem(row.anchor)) return false;
  return !isTerminal(driverOf(row));
}

export function rowStageOf(row: PipelineRow): PipelineStage {
  return pipelineStageOf(rowStageItemOf(row));
}

/* ── time ──────────────────────────────────────────────────────────────── */

/**
 * The newest thing the run recorded, as the list reads it off the work item.
 * Additive on the wire (joined by `GET /api/work-items`), so a bundle talking
 * to a backend that predates it gets `undefined` and nothing is inferred.
 */
export function lastEventOf(item: WorkItem): CodingEvent | null {
  return (
    (item as WorkItem & { latestRunLastEvent?: CodingEvent | null }).latestRunLastEvent ?? null
  );
}

function newest(stamps: readonly (string | null | undefined)[]): string | null {
  let best: string | null = null;
  let bestAt = -Infinity;
  for (const stamp of stamps) {
    if (!stamp) continue;
    const at = Date.parse(stamp);
    if (Number.isFinite(at) && at > bestAt) {
      best = stamp;
      bestAt = at;
    }
  }
  return best;
}

/** When the newest thing happened to this ticket, from every stamp it carries. */
export function activityAtOf(item: WorkItem): string {
  // The run's own event carries its clock as epoch milliseconds.
  const lastEventAt = lastEventOf(item)?.at;
  return (
    newest([
      item.updatedAt,
      item.currentGateOpenedAt,
      item.latestRunStartedAt,
      item.latestRunFinishedAt,
      lastEventAt != null ? new Date(lastEventAt).toISOString() : null,
    ]) ?? item.createdAt
  );
}

/** When the open ask was raised: the age a row wears on the queue. */
export function waitingSinceOf(item: WorkItem): string {
  return item.currentGateOpenedAt ?? rowAgeOf(item);
}

/**
 * The one age a row prints, and what it means follows the view: time in
 * stage on the map, time waiting on the queue, time since the event on the log.
 */
export function rowAgeFor(view: WorkView, item: WorkItem): string {
  if (view === "waiting" && isNeedsYou(item)) return waitingSinceOf(item);
  if (view === "latest") return activityAtOf(item);
  return rowAgeOf(item);
}

function timeOf(iso: string): number {
  const at = Date.parse(iso);
  return Number.isFinite(at) ? at : 0;
}

/* ── By stage ──────────────────────────────────────────────────────────── */

/** "This week" for the Done section's printed rows. */
export const DONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export interface StageSection {
  stage: PipelineStage;
  /** Waiting on a person first, then the most recent activity. */
  rows: PipelineRow[];
  /** Done only: the record older than a week, folded. */
  older: PipelineRow[];
}

/** Waiting on the reader first, then the most recent activity first. */
function byWaitingThenRecent(viewerUserId: string | null) {
  return (a: PipelineRow, b: PipelineRow): number => {
    const wa = isWaitingOnViewer(driverOf(a), viewerUserId) ? 0 : 1;
    const wb = isWaitingOnViewer(driverOf(b), viewerUserId) ? 0 : 1;
    if (wa !== wb) return wa - wb;
    return timeOf(activityAtOf(driverOf(b))) - timeOf(activityAtOf(driverOf(a)));
  };
}

/**
 * The map: one section per stage that has anything in it, in pipeline order.
 * Done keeps this week on the page and folds the rest.
 */
export function stageSections(
  rows: PipelineRow[],
  now: number,
  viewerUserId: string | null,
): StageSection[] {
  const buckets = new Map<PipelineStage, PipelineRow[]>();
  for (const row of rows) {
    const stage = rowStageOf(row);
    const bucket = buckets.get(stage);
    if (bucket) bucket.push(row);
    else buckets.set(stage, [row]);
  }
  const out: StageSection[] = [];
  for (const stage of PIPELINE_STAGES) {
    const bucket = buckets.get(stage);
    if (!bucket || bucket.length === 0) continue;
    const sorted = [...bucket].sort(byWaitingThenRecent(viewerUserId));
    if (stage !== "done") {
      out.push({ stage, rows: sorted, older: [] });
      continue;
    }
    const floor = now - DONE_WEEK_MS;
    const week = sorted.filter((row) => timeOf(activityAtOf(driverOf(row))) >= floor);
    const older = sorted.filter((row) => !week.includes(row));
    out.push({ stage, rows: week, older });
  }
  return out;
}

/* ── Waiting on you ────────────────────────────────────────────────────── */

/**
 * What an open ask is asking, in the order the queue prints the groups.
 * `other` holds any ask outside the four the product names (a verdict to
 * publish, a fix round out of rounds), so no open ask is ever hidden.
 */
export const ASK_KINDS = ["plan", "stopped", "proposal", "merge", "other"] as const;
export type AskKind = (typeof ASK_KINDS)[number];

const ASK_OF_GATE: Record<string, AskKind> = {
  accept_proposal: "proposal",
  approve_execution_plan: "plan",
  approve_worker_instruction: "plan",
  needs_input: "stopped",
  review_result: "merge",
};

const ASK_OF_ACT: Record<string, AskKind> = {
  accept_proposal: "proposal",
  proceed_to_build: "plan",
  merge: "merge",
  approve_pr: "merge",
};

export function askKindOf(item: WorkItem): AskKind {
  const act = item.currentGateAct;
  if (act && ASK_OF_ACT[act]) return ASK_OF_ACT[act];
  return ASK_OF_GATE[item.currentGateKind ?? ""] ?? "other";
}

export interface AskGroup {
  kind: AskKind;
  /** Oldest ask first. */
  rows: PipelineRow[];
}

export interface WaitingGroups {
  /** Open asks addressed to the reader, by what they ask. */
  asks: AskGroup[];
  /**
   * Open asks addressed to somebody else (or, on a gate older than the rule
   * that every gate names a person, to nobody), folded to one line and oldest
   * first. Nothing that waits is hidden; it just is not the reader's queue.
   */
  others: PipelineRow[];
  /** Everything moving or queued, folded to one line. */
  working: PipelineRow[];
  /** Done this week, folded to one line. */
  done: PipelineRow[];
}

/**
 * How many rows wait on the reader: the Waiting on you tab, the masthead's
 * "N waiting", and the sidebar pill all print this one number.
 */
export function waitingOnViewerCount(rows: PipelineRow[], viewerUserId: string | null): number {
  return rows.filter((row) => isWaitingOnViewer(driverOf(row), viewerUserId)).length;
}

export function waitingGroups(
  rows: PipelineRow[],
  now: number,
  viewerUserId: string | null,
  /** A search keeps done rows older than a week: what it found must print. */
  options: { keepOlderDone?: boolean } = {},
): WaitingGroups {
  const byKind = new Map<AskKind, PipelineRow[]>();
  const others: PipelineRow[] = [];
  const working: PipelineRow[] = [];
  const done: PipelineRow[] = [];
  const floor = now - DONE_WEEK_MS;
  for (const row of rows) {
    const driver = driverOf(row);
    if (isNeedsYou(driver) && !isWaitingOnViewer(driver, viewerUserId)) {
      others.push(row);
      continue;
    }
    if (isNeedsYou(driver)) {
      const kind = askKindOf(driver);
      const bucket = byKind.get(kind);
      if (bucket) bucket.push(row);
      else byKind.set(kind, [row]);
      continue;
    }
    if (rowStateOf(driver) === "settled") {
      if (options.keepOlderDone || timeOf(activityAtOf(driver)) >= floor) done.push(row);
      continue;
    }
    working.push(row);
  }
  const oldestFirst = (a: PipelineRow, b: PipelineRow) =>
    timeOf(waitingSinceOf(driverOf(a))) - timeOf(waitingSinceOf(driverOf(b)));
  const recentFirst = (a: PipelineRow, b: PipelineRow) =>
    timeOf(activityAtOf(driverOf(b))) - timeOf(activityAtOf(driverOf(a)));
  const asks: AskGroup[] = [];
  for (const kind of ASK_KINDS) {
    const bucket = byKind.get(kind);
    if (bucket && bucket.length > 0) asks.push({ kind, rows: [...bucket].sort(oldestFirst) });
  }
  return {
    asks,
    others: others.sort(oldestFirst),
    working: working.sort(recentFirst),
    done: done.sort(recentFirst),
  };
}

/* ── Latest ────────────────────────────────────────────────────────────── */

export interface LatestDay {
  /** Local midnight of the day, as a timestamp. */
  day: number;
  rows: PipelineRow[];
}

export interface LatestBands {
  /** Events after the reader last looked. Null when they never have. */
  since: PipelineRow[] | null;
  /** The rest of today. */
  today: PipelineRow[];
  /** Every older day, newest first, one fold each. */
  days: LatestDay[];
}

function startOfDay(at: number): number {
  const d = new Date(at);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function latestBands(
  rows: PipelineRow[],
  lastLooked: number | null,
  now: number,
): LatestBands {
  const sorted = [...rows].sort(
    (a, b) => timeOf(activityAtOf(driverOf(b))) - timeOf(activityAtOf(driverOf(a))),
  );
  const todayFloor = startOfDay(now);
  const since: PipelineRow[] = [];
  const today: PipelineRow[] = [];
  const days = new Map<number, PipelineRow[]>();
  for (const row of sorted) {
    const at = timeOf(activityAtOf(driverOf(row)));
    if (lastLooked != null && at > lastLooked) since.push(row);
    else if (at >= todayFloor) today.push(row);
    else {
      const day = startOfDay(at);
      const bucket = days.get(day);
      if (bucket) bucket.push(row);
      else days.set(day, [row]);
    }
  }
  return {
    since: lastLooked == null ? null : since,
    today,
    days: [...days.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([day, dayRows]) => ({ day, rows: dayRows })),
  };
}

/* ── the pager's walk ──────────────────────────────────────────────────── */

/**
 * Every row in the order the view prints it, folded groups included, so the
 * case file's Item N of M pager walks what the reader was reading.
 */
export function viewOrder(
  rows: PipelineRow[],
  view: WorkView,
  opts: { now: number; lastLooked: number | null; viewerUserId: string | null },
): WorkItem[] {
  if (view === "waiting") {
    const groups = waitingGroups(rows, opts.now, opts.viewerUserId);
    return [
      ...groups.asks.flatMap((group) => group.rows),
      ...groups.others,
      ...groups.working,
      ...groups.done,
    ].map((row) => row.anchor);
  }
  if (view === "latest") {
    const bands = latestBands(rows, opts.lastLooked, opts.now);
    return [...(bands.since ?? []), ...bands.today, ...bands.days.flatMap((day) => day.rows)].map(
      (row) => row.anchor,
    );
  }
  return stageSections(rows, opts.now, opts.viewerUserId)
    .flatMap((section) => [...section.rows, ...section.older])
    .map((row) => row.anchor);
}

/* ── the row's sentence on the log ─────────────────────────────────────── */

/**
 * The Latest view's sentence: it names the EVENT, because on the log the
 * reader is asking what happened, not where the ticket stands. "Bailey
 * proposed it, waiting on you". "Wright: pushed the 3rd commit". Anything the
 * record cannot name as an event reads as its state sentence, stage word
 * included, since the log has no stage heading above it.
 */
export function eventSentenceOf(
  item: WorkItem,
  workerName: string | null,
  viewerUserId: string | null,
): string {
  const agent = item.currentGateAgentName?.trim() || workerName;
  switch (rowStateOf(item)) {
    case "waiting": {
      const who = waitingOnWhom(item, viewerUserId);
      const kind = askKindOf(item);
      // A ticket a person assigned to yagni is theirs, not the Agent's proposal.
      if (kind === "proposal" && item.currentGateJobKind === "pm.take_assigned") {
        const first = gateJobLineOf(item).assignedByName?.trim().split(/\s+/)[0];
        return `${first || "A teammate"} assigned it, waiting on ${who}`;
      }
      if (kind === "proposal")
        return `${agent ?? "A proposal"} ${agent ? "proposed it" : "came in"}, waiting on ${who}`;
      if (kind === "plan")
        return `${agent ?? "The plan"} ${agent ? "drafted the plan" : "is drafted"}, waiting on ${who}`;
      if (kind === "stopped")
        return `${agent ? `${agent} stopped to ask` : "stopped to ask"}, waiting on ${who}`;
      if (kind === "merge") return `review is in, waiting on ${who} to merge`;
      const phrase = itemWaitingPhrase(item);
      return phrase ? `waiting on ${who} to ${phrase}` : `waiting on ${who}`;
    }
    case "working": {
      const last = lastEventOf(item);
      const line = last ? runLogLines([last])[0] : undefined;
      if (line) return agent ? `${agent}: ${line.text}` : line.text;
      return WORKING_CLAUSE[pipelineStageOf(item)](agent);
    }
    case "settled":
      if (item.phase === "dismissed") return "closed, no work was done";
      if (item.phase === "split") return "split into its own tickets";
      if (item.phase === "done") return item.prUrl ? "shipped, merged" : "shipped";
      // A finished QA check on a ticket that is still moving.
      return (
        rowClauseOf(item, workerName, viewerUserId) ?? PIPELINE_STAGE_LABEL[pipelineStageOf(item)]
      );
    default: {
      const clause = rowClauseOf(item, workerName, viewerUserId);
      return clause
        ? `${PIPELINE_STAGE_LABEL[pipelineStageOf(item)]} · ${clause}`
        : rowStateLine(item, workerName, viewerUserId);
    }
  }
}
